import type { ReactNode } from 'react';
import { defineMessages } from 'react-intl';
import styled, { css } from 'styled-components';

import { Dot } from 'atoms/icons/Dot';
import { Accordion } from 'atoms/layout/Accordion';
import { Horizontal, SBHorizontal, Vertical } from 'atoms/layout/flex';
import { HeadlineXS, LabelM } from 'atoms/typography';
import { useIntlContext } from 'contexts/intl';

interface Props {
  title: string | ReactNode;
  subtitle?: ReactNode;
  date: Date | null;
  state?: ReactNode;
  action?: ReactNode;
  children?: ReactNode;
  amount: ReactNode;
  icon?: ReactNode;
  expandable?: boolean;
}

export const messages = defineMessages({
  offerTitleAsReceiver: {
    id: 'TransactionsHistoryOffer.titleAsReceiver',
    defaultMessage: 'Sale from {otherParty}',
  },
  offerTitleAsSender: {
    id: 'TransactionsHistoryOffer.titleAsSender',
    defaultMessage: 'Sale to {otherParty}',
  },
});

const Infos = styled(Horizontal)`
  color: var(--c-nd-600);
`;

const Header = styled(SBHorizontal).attrs({ gap: 0 })`
  flex: 1;
  text-align: left;
`;

const NotExpandableBlock = styled.div<{ rightPadding?: boolean }>`
  --collapse-icon-width: 14px;
  --gap: var(--double-unit);
  --right-padding: calc(var(--gap) + var(--collapse-icon-width));

  display: flex;
  justify-content: space-between;
  padding: var(--intermediate-unit) 0;
  border-bottom: 1px solid var(--c-nd-50);

  ${({ rightPadding }) =>
    rightPadding
      ? css`
          padding-right: var(--right-padding);
        `
      : null};
`;

const Block = styled.div`
  display: flex;
  gap: var(--unit);
  align-items: flex-end;
`;

const Children = styled(SBHorizontal).attrs({ gap: 0 })`
  width: 100%;
`;

const ChildrenContent = styled.div`
  width: 100%;
  border-left: 5px solid var(--c-nd-200);
  padding-left: var(--double-unit);
`;

export const TransactionEntry = ({
  title,
  date,
  amount,
  state,
  children,
  action,
  icon,
  subtitle,
  expandable = true,
}: Props) => {
  const { formatDate } = useIntlContext();

  const renderHeader = () => (
    <Header>
      <Block>
        {icon}
        <Vertical gap={0}>
          <Horizontal>
            {!!title && <HeadlineXS as="h6">{title}</HeadlineXS>}
            {state}
          </Horizontal>
          {!!date && (
            <Infos>
              <LabelM>
                {formatDate(date, {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  minute: 'numeric',
                  hour: 'numeric',
                })}
              </LabelM>
              {!!subtitle && (
                <>
                  <Dot size={0.5} />
                  {subtitle}
                </>
              )}
            </Infos>
          )}
        </Vertical>
      </Block>
      {amount}
    </Header>
  );

  const renderChildren = () => (
    <Children>
      <ChildrenContent>{children}</ChildrenContent>
      {action}
    </Children>
  );

  return (
    <>
      {(children || action) && expandable ? (
        <Accordion noHorizontalPadding title={renderHeader()}>
          {renderChildren()}
        </Accordion>
      ) : (
        <NotExpandableBlock rightPadding={expandable}>
          {renderHeader()}
        </NotExpandableBlock>
      )}
    </>
  );
};
